import { defineStore } from 'pinia'
import { nextTick, ref } from 'vue'
import { useId } from 'vue'

export interface Wrapper {
  id: string
  title: string
  disabled: boolean
  toggeable: boolean
  children?: Wrapper[]
  afterPrint?: () => void
  beforePrint?: () => void
}

export function wrapperDefaults(): Wrapper {
  return {
    id: useId(),
    title: useId(),
    disabled: true,
    toggeable: false,
    children: [],
  }
}

/**
 * Handle the execution of functions before and after page printing to manage
 * elements that cannot be trivially hidden via CSS print media queries.
 */
export const usePrintingStore = defineStore(
  'printing',
  () => {
    const pageTitle = ref(document.title)

    const wrappers = ref<Wrapper[]>([])

    function $reset() {
      wrappers.value = []
    }

    const registerHooks = (wrapper: Partial<Wrapper>) => {
      wrappers.value.push({
        ...wrapperDefaults(),
        ...wrapper,
      })
    }

    const beforePrint = (wrappers: Wrapper[]) => {
      wrappers.forEach((w) => {
        if (w.children?.length) beforePrint(w.children)
        if (!w.disabled) w.beforePrint?.()
      })
    }

    const afterPrint = (wrappers: Wrapper[]) => {
      wrappers.forEach((w) => {
        if (w.children?.length) afterPrint(w.children)
        if (!w.disabled) w.afterPrint?.()
      })
    }

    const print = async (filename?: string) => {
      if (filename) document.title = filename

      beforePrint(wrappers.value)

      // This class is set by Vuetify's VDialog component and it messes up the printing layout
      document.documentElement.classList.remove('v-overlay-scroll-blocked')

      // Force a re-render so the changes made by the `beforePrint` callbacks appear on printing
      await nextTick()

      window.print()

      afterPrint(wrappers.value)

      if (filename) document.title = pageTitle.value
    }

    return { $reset, registerHooks, print, items: wrappers }
  },
)
